import { useContext } from 'react'

import {
  useMasterDeleteAllApi,
  useMasterSearchApi,
  useMasterSearchCondition
} from '@/common/api/modules/master'
import { useFirstLoading } from '@/common/api/search'
import { masterTypeName } from '@/common/app/modules/master'
import { w2MasterNewPath } from '@/common/app/path'
import { useSiteId } from '@/common/app/site'
import { useShowDeletedToast } from '@/common/app/toast'
import { MasterType } from '@/common/model/master/w2Master'
import { useSelectIds } from '@/common/util/select'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import DisclosureRow from '@/components/view/atoms/disclosure/DisclosureRow'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageListLoading from '@/components/view/molecules/loading/PageListLoading'
import PageListSearching from '@/components/view/molecules/loading/PageListSearching'
import NoResultGuidance from '@/components/view/molecules/search/NoResultGuidance'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import AppTitleBadge from '@/components/container/layout/AppTitleBadge'
import CsvUploadModalProvider, {
  CsvUploadModalContext
} from '@/components/container/modal/csv/CsvUploadModalProvider'
import { ConfirmDialogContext } from '@/components/container/modal/dialog/confirm/ConfirmDialogProvider'
import FeaturePageImageAlert from '@/components/container/modules/master/FeaturePageImageAlert'
import MasterListTable from '@/components/container/modules/master/master/table/MasterListTable'
import MasterSearchConditionField from '@/components/container/modules/master/master/table/MasterSearchConditionField'
import OperationButton from '@/components/container/modules/master/operation/OperationButton'
import TitlePageInfo from '@/components/container/utils/pager/TitlePageInfo'

const CsvUploadOperationButton = ({
  siteId,
  masterType,
  onClose
}: {
  siteId: number
  masterType: MasterType
  onClose: () => void
}) => {
  const { showModal: showUploadModal } = useContext(CsvUploadModalContext)

  return (
    <Button
      onClick={() => showUploadModal({ siteId, masterType, onClose })}
      color={ButtonColor.SECONDARY}
      size='large'
    >
      CSVアップロード
    </Button>
  )
}

interface Props {
  masterType: MasterType
}

const MasterListPage = ({ masterType }: Props) => {
  const siteId = useSiteId()
  const [condition, setCondition, onUpdatePage] = useMasterSearchCondition()
  const { data, error, isLoading, mutate } = useMasterSearchApi(siteId, masterType, condition)
  const { selectedIds, setSelectedIds, onSelectId, onSelectAll } = useSelectIds(
    data ? data.items : [],
    condition
  )

  const showDelete = useShowDeletedToast()
  const { showModal, hide } = useContext(ConfirmDialogContext)
  const { trigger: deleteAll } = useMasterDeleteAllApi({
    onSuccess: () => {
      mutate()
      onUpdatePage(1)
      hide()
      showDelete(`${masterTypeName(masterType)}を全て`)
    }
  })

  const isFirstLoading = useFirstLoading(isLoading)

  return (
    <CsvUploadModalProvider>
      <Main type={MainLayoutType.FULL}>
        {/* V5区分サイトは画像がマスタ連携で届かず公開ページから取得している。
            取得できなかったページを手動で登録する導線を、この一覧に出す */}
        {masterType === MasterType.W2_FEATURE_PAGE_CONTENTS && (
          <FeaturePageImageAlert siteId={siteId} />
        )}
        <PageTitle
          title={masterTypeName(masterType) + 'マスタ一覧'}
          badgeNode={<AppTitleBadge type='master' />}
          description={`W2Commerceから連携されている${masterTypeName(masterType)}マスタ`}
          behindNode={
            data && (
              <TitlePageInfo
                page={condition.page}
                perPage={condition.perPage}
                total={data.total}
                unit={masterTypeName(masterType).replace('W2', '')}
              />
            )
          }
          rightNode={
            <>
              <CsvUploadOperationButton
                siteId={siteId}
                masterType={masterType}
                onClose={() => {
                  mutate()
                  onUpdatePage(1)
                }}
              />
              <Button
                link={w2MasterNewPath(siteId, masterType)}
                size='large'
                color={ButtonColor.PRIMARY_BRAND}
              >
                {masterTypeName(masterType)}を追加する
              </Button>
              <OperationButton
                node={
                  <DisclosureRow
                    onClick={() => {
                      showModal({
                        message: `${masterTypeName(masterType)}を一括削除します。よろしいですか？`,
                        noText: 'キャンセル',
                        yesText: '削除する',
                        onClickYes: async () => {
                          await deleteAll({
                            site_id: siteId,
                            master_type: masterType
                          })
                          return
                        }
                      })
                    }}
                    disabled={data ? data.total === 0 : true}
                  >
                    <Text
                      lineHeight={150}
                      color={
                        data
                          ? data.total === 0
                            ? TextColor.DISABLED
                            : TextColor.PRIMARY
                          : TextColor.DISABLED
                      }
                    >
                      一括削除する
                    </Text>
                  </DisclosureRow>
                }
              />
            </>
          }
        />
        <Content>
          {isFirstLoading ? (
            <PageListLoading />
          ) : (
            <Area tableWrap noBorder>
              <MasterSearchConditionField condition={condition} setCondition={setCondition} />
              {isLoading ? (
                <PageListSearching />
              ) : data?.total ? (
                <>
                  <MasterListTable
                    siteId={siteId}
                    masterType={masterType}
                    items={data.items}
                    reload={() => {
                      mutate()
                      setSelectedIds([])
                    }}
                    selectedIds={selectedIds}
                    onSelect={onSelectId}
                    onSelectAll={onSelectAll}
                    condition={condition}
                    setCondition={setCondition}
                    onUpdatePage={onUpdatePage}
                    total={data.total}
                  />
                </>
              ) : (
                <NoResultGuidance
                  searched={!!condition.keyword}
                  name={masterTypeName(masterType)}
                />
              )}
            </Area>
          )}
        </Content>
      </Main>
    </CsvUploadModalProvider>
  )
}

export default MasterListPage
